<template>
  <div class="video-page">
    <header class="page-top">
      {{ $t('videoPage.title') }}
    </header>
    <div class="filters">
      <div class="filter-row">
        <button
          :class="{ active: classId === 0 }"
          :aria-pressed="classId === 0"
          @click="selectCategory(0)"
        >
          {{ $t('videoPage.all') }}
        </button>
        <button
          v-for="category in categories"
          :key="category.id"
          :class="{ active: classId === Number(category.id) }"
          :aria-pressed="classId === Number(category.id)"
          @click="selectCategory(Number(category.id))"
        >
          {{ category.name }}
        </button>
      </div>
      <div class="filter-row">
        <button
          v-for="option in sortOptions"
          :key="option.value"
          :class="{ active: sort === option.value }"
          :aria-pressed="sort === option.value"
          @click="selectSort(option.value)"
        >
          {{ $t(option.label) }}
        </button>
      </div>
    </div>
    <main
      ref="content"
      class="page-content"
    >
      <van-list
        v-model="loading"
        :finished="finished"
        :error.sync="loadError"
        :immediate-check="false"
        :offset="100"
        :loading-text="$t('videoPage.loading')"
        :finished-text="list.length ? $t('videoPage.finished') : ''"
        :error-text="$t('videoPage.retry')"
        @load="loadVideos"
      >
        <div class="video-grid">
          <a
            v-for="item in list"
            :key="item.id"
            class="video-card"
            :href="'#/vplay/' + item.id"
            @click.prevent="$router.push('/vplay/' + item.id)"
          >
            <div class="cover">
              <img
                :src="formatAssetUrl(item.pic)"
                alt=""
                loading="lazy"
              >
              <span class="score">{{ scoreText(item.score) }}</span>
            </div>
            <div class="video-title">{{ item.title }}</div>
          </a>
        </div>
      </van-list>
      <van-empty
        v-if="finished && !list.length && !loadError"
        :description="$t('videoPage.empty')"
      />
    </main>
  </div>
</template>

<script>
import { Empty, List } from 'vant';
import Fetch from '../../utils/fetch';
import { formatAssetUrl } from '../../utils/assets';

export default {
  name: 'Vindex',
  components: { 'van-empty': Empty, 'van-list': List },
  data() {
    return {
      categories: [],
      classId: 0,
      sort: 'default',
      sortOptions: [
        { value: 'default', label: 'videoPage.all' },
        { value: 'score_desc', label: 'videoPage.highest' },
        { value: 'score_asc', label: 'videoPage.lowest' }
      ],
      list: [],
      page: 1,
      loading: false,
      finished: false,
      loadError: false,
      requestLock: false,
      requestVersion: 0
    };
  },
  created() {
    this.$parent.footer('video', true);
  },
  mounted() {
    if (!localStorage.getItem('token')) {
      this.$router.replace({ path: '/login', query: { redirect: this.$route.fullPath } });
      return;
    }
    this.loadVideos();
  },
  beforeDestroy() {
    this.requestVersion++;
  },
  methods: {
    formatAssetUrl,
    scoreText(score) {
      return score === null || score === undefined || score === ''
        ? this.$t('videoPage.unrated')
        : this.$t('videoPage.score', { score });
    },
    selectCategory(id) {
      if (this.classId === id) return;
      this.classId = id;
      this.resetVideos();
    },
    selectSort(sort) {
      if (this.sort === sort) return;
      this.sort = sort;
      this.resetVideos();
    },
    resetVideos() {
      this.requestVersion++;
      this.requestLock = false;
      this.list = [];
      this.page = 1;
      this.finished = false;
      this.loadError = false;
      this.$refs.content.scrollTop = 0;
      this.loadVideos();
    },
    loadVideos() {
      if (this.requestLock || this.finished) return;
      const version = this.requestVersion;
      this.requestLock = true;
      this.loading = true;
      this.loadError = false;
      Fetch('/index/videos', { class_id: this.classId, sort: this.sort, page: this.page })
        .then(response => {
          if (version !== this.requestVersion) return;
          const data = response.data;
          if (!data || !Array.isArray(data.list)) throw new Error('Invalid video response');
          // Older deployed APIs return the paginated list but do not include categories.
          // Keep the default “All” filter usable until those APIs are upgraded.
          this.categories = Array.isArray(data.categories) ? data.categories : [];
          this.list = this.list.concat(data.list);
          this.page++;
          this.finished = !data.has_more;
        })
        .catch(() => {
          if (version === this.requestVersion) this.loadError = true;
        })
        .finally(() => {
          if (version === this.requestVersion) {
            this.loading = false;
            this.requestLock = false;
          }
        });
    }
  }
};
</script>

<style lang="less" scoped>
.video-page {
  height: 100vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #211d1d;
  color: #f4ece7;
}
.page-top {
  flex: 0 0 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffc785;
  font-size: 14px;
}
.filters { background: #2b2b2b; flex-shrink: 0; }
.filter-row {
  display: flex;
  align-items: center;
  gap: 18px;
  height: 40px;
  padding: 0 12px;
  overflow-x: auto;
  scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }
  & + & { border-top: 1px solid #353131; }
  button {
    flex-shrink: 0;
    border: 0;
    border-radius: 20px;
    padding: 3px 9px;
    background: transparent;
    color: #f4ece7;
    font: inherit;
    font-size: 14px;
    line-height: 18px;
    white-space: nowrap;
    cursor: pointer;
    &.active { background: #f5cda1; color: #744d2e; }
    &:focus-visible { outline: 2px solid #ffc785; outline-offset: 2px; }
  }
}
.page-content {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding-bottom: calc(100px + env(safe-area-inset-bottom));
  -webkit-overflow-scrolling: touch;
}
.video-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px 10px;
  padding: 14px 8px;
}
.video-card { min-width: 0; color: inherit; text-decoration: none; }
.cover {
  position: relative;
  overflow: hidden;
  border-radius: 12px;
  padding-top: 66.6667%;
  background: #343030;
  img { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; }
}
.score {
  position: absolute;
  right: 5px;
  bottom: 6px;
  max-width: calc(100% - 10px);
  box-sizing: border-box;
  border-radius: 7px 0 7px 0;
  padding: 2px 6px;
  background: rgba(0, 0, 0, .65);
  color: #fff;
  font-size: 12px;
  line-height: 16px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.video-title { margin-top: 9px; font-size: 12px; line-height: 18px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
/deep/ .van-empty__description,
/deep/ .van-list__loading,
/deep/ .van-list__finished-text,
/deep/ .van-list__error-text { color: #c2b5ab; }
</style>
